import React, {useEffect, useMemo, useState} from "react";
import styled, {css} from "styled-components";
import {Link, useLocation, useNavigate, useParams} from "react-router-dom";
import {useAtom, useAtomValue} from "jotai";
import {adminInfoAtom, tokenResultAtom} from "../../store";
import {logo_inline_w} from "../../constants/GlobalConst";
import {menuList, ROLE} from "./entity";
import {selectUserCategories} from "../../services/platform/ManageUserCategoryAxios";

function MenuGlyph({name}) {
  const props = {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.8,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": true,
  };

  switch (name) {
    case "dashboard":
      return <svg {...props}><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>;
    case "reports":
      return <svg {...props}><path d="M4 20V10"/><path d="M10 20V4"/><path d="M16 20v-7"/><path d="M22 20H2"/></svg>;
    case "media":
      return <svg {...props}><rect x="3" y="4" width="18" height="16" rx="2"/><path d="M3 9h18M8 9v11"/></svg>;
    case "account":
      return <svg {...props}><path d="M4 7h16a1 1 0 0 1 1 1v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h12"/><path d="M16 12h5v4h-5a2 2 0 0 1 0-4Z"/></svg>;
    case "platform":
      return <svg {...props}><path d="m12 3 9 5-9 5-9-5 9-5Z"/><path d="m3 12 9 5 9-5"/><path d="m3 16 9 5 9-5"/></svg>;
    case "notice":
      return <svg {...props}><path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9"/><path d="M10 21h4"/></svg>;
    default:
      return null;
  }
}

function canAccessMainMenu(item, role) {
  if ([ROLE.ADMIN, ROLE.SUPER_ADMIN].includes(role)) {
    return true;
  }
  return role === ROLE.NORMAL && ["dashboard", "reports", "account", "notice", "apiGuide"].includes(item.name);
}

function canAccessChildMenu(child, role, convertedUser) {
  if (role === ROLE.SUPER_ADMIN) {
    return convertedUser !== "" || child.name !== "accountProfile";
  }
  if (role === ROLE.ADMIN) {
    if (convertedUser !== "") {
      return !["reportsMedia", "platformAdmin", "platformTerm", "platformHistory"].includes(child.name);
    }
    return !["accountProfile", "platformAdmin", "platformTerm", "platformHistory"].includes(child.name);
  }
  return role === ROLE.NORMAL &&
    ["reports", "reportsHour", "reportsInventory", "reportsAdExchange", "account", "accountHistory", "apiGuide", "apiTest"].includes(child.name);
}

function AsideList({id, collapsed, role}) {
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [reportCategories, setReportCategories] = useState([]);
  const [expandedSubMenu, setExpandedSubMenu] = useState(null);
  const adminInfoState = useAtomValue(adminInfoAtom);
  const convertedUser = adminInfoState.convertedUser || "";
  const selectedCategoryId = new URLSearchParams(location.search).get("categoryId");

  useEffect(() => {
    let active = true;
    selectUserCategories()
      .then(categories => active && setReportCategories(categories))
      .catch(() => active && setReportCategories([]));
    return () => {active = false};
  }, []);

  useEffect(() => {
    if (params?.id === undefined) {
      navigate("/");
    }
  }, [navigate, params?.id]);

  useEffect(() => {
    if (params.id === "accountProfile" && convertedUser === "") {
      navigate("/board/account");
    }
  }, [convertedUser, navigate, params.id]);

  const availableMenus = useMemo(
    () => menuList.filter(item => canAccessMainMenu(item, role)),
    [role]
  );

  return availableMenus.map((item, index) => {
    const active = !item.disabled && item.include.includes(id);
    const previousGroup = availableMenus[index - 1]?.group;
    const showGroupLabel = item.group !== previousGroup;
    const children = item.child.filter(child => canAccessChildMenu(child, role, convertedUser));

    return (
      <React.Fragment key={item.name}>
        {showGroupLabel && !collapsed && (
          <MenuSectionLabel $support={item.group === "support"}>
            {item.groupLabel}
          </MenuSectionLabel>
        )}
        {showGroupLabel && collapsed && item.group === "support" && <SupportSpacer/>}
        <MenuItem $active={active} $support={item.group === "support"}>
          <MenuLink
            as={item.disabled ? "div" : undefined}
            to={item.disabled ? undefined : `/board/${item.name}`}
            $active={active}
            $collapsed={collapsed}
            $support={item.group === "support"}
            $disabled={item.disabled}
            aria-disabled={item.disabled || undefined}
            title={item.disabled ? `${item.header} (준비 중)` : (collapsed ? item.header : undefined)}
          >
            <MenuIcon $active={active} $support={item.group === "support"}>
              {item.header === "도움말" ? <HelpGlyph>?</HelpGlyph> : <MenuGlyph name={item.name}/>}
            </MenuIcon>
            <MenuLabel $collapsed={collapsed} $support={item.group === "support"}>{item.header}</MenuLabel>
            {!collapsed && children.length > 0 && <DropIcon $open={active}/>}
          </MenuLink>

          {children.length > 0 && (
            <SubMenu $active={active} $collapsed={collapsed}>
              {children.map(child => {
                const selected = id === child.name || id === child.detail;
                const hasCategoryMenu = ["reportsMedia", "platform", "mediaList"].includes(child.name);
                const categoryMenuOpen = expandedSubMenu === child.name;
                if (hasCategoryMenu) {
                  const basePath = `/board/${child.name}`;
                  const allLabel = child.name === "platform" ? "전체 사용자"
                      : child.name === "mediaList" ? "전체 지면" : "전체 매체";
                  return (
                    <NestedMenuGroup key={child.name}>
                      <SubMenuItem $selected={selected}>
                        <SubMenuToggle
                          type="button"
                          $selected={selected}
                          onClick={() => {
                            const isAllPageSelected = selected && !selectedCategoryId;
                            if (!isAllPageSelected) {
                              navigate(basePath);
                              setExpandedSubMenu(null);
                              return;
                            }
                            setExpandedSubMenu(current => current === child.name ? null : child.name);
                          }}
                        >
                          <span>{child.header}</span>
                          <NestedArrow $open={categoryMenuOpen}/>
                        </SubMenuToggle>
                      </SubMenuItem>
                      <NestedSubMenu $open={categoryMenuOpen}>
                        <NestedSubMenuLink to={basePath} $selected={selected && !selectedCategoryId}>
                          {allLabel}
                        </NestedSubMenuLink>
                        {reportCategories.map(category => (
                          <NestedSubMenuLink
                            key={category.id}
                            to={`${basePath}?categoryId=${category.id}`}
                            $selected={selected && String(selectedCategoryId) === String(category.id)}
                          >
                            {category.label}
                          </NestedSubMenuLink>
                        ))}
                      </NestedSubMenu>
                    </NestedMenuGroup>
                  );
                }
                return (
                  <SubMenuItem key={child.name} $selected={selected}>
                    <SubMenuLink to={`/board/${child.name}`} $selected={selected}>
                      {child.header}
                    </SubMenuLink>
                  </SubMenuItem>
                );
              })}
            </SubMenu>
          )}
        </MenuItem>
      </React.Fragment>
    );
  });
}

function Aside({mobileOpen = false, onNavigate}) {
  const params = useParams();
  const [collapsed, setCollapsed] = useState(false);
  const [tokenUserInfo] = useAtom(tokenResultAtom);

  useEffect(() => {
    onNavigate?.();
    // 경로 이동 시 모바일 메뉴를 닫습니다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.id]);

  return (
    <AsideRoot $collapsed={collapsed} $mobileOpen={mobileOpen}>
      <AsideContainer>
        <AsideHeader $collapsed={collapsed}>
          <Link to="/board/dashboard" aria-label="대시보드로 이동">
            {collapsed ? (
              <LogoCompact>MP</LogoCompact>
            ) : (
              <LogoTextGroup>
                <Logo/>
                <LogoDescription>광고 관리 플랫폼(Oaple-Media)</LogoDescription>
              </LogoTextGroup>
            )}
          </Link>
        </AsideHeader>

        <MenuSection>
          <Menu>
            <AsideList id={params.id} collapsed={collapsed} role={tokenUserInfo.role}/>
          </Menu>
        </MenuSection>

        <AsideFooter>
          <CollapseButton type="button" $collapsed={collapsed} onClick={() => setCollapsed(value => !value)}>
            <CollapseIcon $collapsed={collapsed}/>
            <CollapseText $collapsed={collapsed}>메뉴 접기</CollapseText>
          </CollapseButton>
        </AsideFooter>
      </AsideContainer>
    </AsideRoot>
  );
}

export default Aside;

const AsideRoot = styled.aside`
  position: relative;
  z-index: 220;
  width: ${({$collapsed}) => ($collapsed ? "76px" : "228px")};
  min-width: ${({$collapsed}) => ($collapsed ? "76px" : "228px")};
  flex: 0 0 ${({$collapsed}) => ($collapsed ? "76px" : "228px")};
  transition: width .22s ease, min-width .22s ease, flex-basis .22s ease;

  @media (max-width: 960px) {
    position: fixed;
    inset: 0 auto 0 0;
    width: 228px;
    min-width: 228px;
    min-height: 100vh;
    flex-basis: 228px;
    transform: translateX(${({$mobileOpen}) => ($mobileOpen ? "0" : "-102%")});
    transition: transform .22s ease;
  }
`;

const AsideContainer = styled.div`
  position: sticky;
  top: 0;
  display: flex;
  width: 100%;
  height: 100vh;
  flex-direction: column;
  overflow: hidden;
  border-right: 1px solid rgba(255, 255, 255, .08);
  background: linear-gradient(180deg, #20242b 0%, #191c22 100%);

  @media (max-width: 960px) {
    width: 228px;
    box-shadow: 18px 0 40px rgba(15, 23, 42, .28);
  }
`;

const AsideHeader = styled.div`
  display: flex;
  min-height: 82px;
  align-items: center;
  justify-content: center;
  padding: ${({$collapsed}) => ($collapsed ? "0" : "0 18px")};

  a {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const Logo = styled.div`
  width: 176px;
  height: 44px;
  background: url(${logo_inline_w}) left center / contain no-repeat;
`;

const LogoTextGroup = styled.div`
  display: flex;
  min-width: 0;
  flex-direction: column;
  align-items: flex-start;
  gap: 3px;
`;

const LogoDescription = styled.span`
  padding-left: 20px;
  color: rgba(255, 255, 255, .48);
  font-size: var(--font-size-12);
  font-weight: 500;
  letter-spacing: -.1px;
  white-space: nowrap;
`;

const LogoCompact = styled.div`
  display: inline-flex;
  width: 40px;
  height: 40px;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(59, 130, 246, .45);
  border-radius: 10px;
  background: rgba(59, 130, 246, .12);
  color: ${({theme}) => theme.color.subColor};
  font-size: var(--font-size-14);
  font-weight: 800;
`;

const MenuSection = styled.nav`
  min-height: 0;
  flex: 1;
  padding: 16px 10px 14px;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, .18) transparent;
`;

const Menu = styled.ul`
  display: flex;
  width: 100%;
  min-height: 100%;
  flex-direction: column;
  margin: 0;
  padding: 0;
  list-style: none;
`;

const MenuSectionLabel = styled.div`
  margin: ${({$support}) => ($support ? "auto 9px 6px" : "8px 9px 9px")};
  padding-top: ${({$support}) => ($support ? "14px" : "0")};
  color: rgba(255, 255, 255, .42);
  font-size: var(--font-size-11);
  font-weight: 700;
  letter-spacing: 1px;
`;

const SupportSpacer = styled.div`
  margin-top: auto;
`;

const MenuItem = styled.li`
  position: relative;
  display: flex;
  width: 100%;
  flex-direction: column;
  margin-bottom: ${({$support}) => ($support ? "2px" : "4px")};
  border-radius: 8px;

  ${({$active}) => $active && css`
    background: rgba(255, 255, 255, .09);

    &::before {
      position: absolute;
      top: 8px;
      left: -10px;
      width: 3px;
      height: 32px;
      border-radius: 0 2px 2px 0;
      background: ${({theme}) => theme.color.subColor};
      content: "";
    }
  `}
`;

const MenuLink = styled(Link)`
  display: grid;
  width: 100%;
  min-height: ${({$support}) => ($support ? "42px" : "46px")};
  grid-template-columns: 34px ${({$collapsed}) => ($collapsed ? "0" : "minmax(0, 1fr)")} ${({$collapsed}) => ($collapsed ? "0" : "18px")};
  align-items: center;
  gap: ${({$collapsed}) => ($collapsed ? "0" : "8px")};
  padding: ${({$collapsed}) => ($collapsed ? "0 11px" : "0 12px")};
  overflow: hidden;
  border-radius: 8px;
  color: ${({$active, theme}) => ($active ? theme.color.subColor : "rgba(255, 255, 255, .68)")};
  text-decoration: none;
  transition: color .15s ease, background-color .15s ease;
  cursor: ${({$disabled}) => ($disabled ? "not-allowed" : "pointer")};
  opacity: ${({$disabled}) => ($disabled ? .42 : 1)};
  user-select: ${({$disabled}) => ($disabled ? "none" : "auto")};

  &:hover {
    background: ${({$disabled}) => ($disabled ? "transparent" : "rgba(255, 255, 255, .07)")};
    color: ${({$active, $disabled, theme}) => ($disabled ? "rgba(255, 255, 255, .68)" : ($active ? theme.color.subColor : "var(--color-surface)"))};
  }

  &:focus-visible {
    outline: 1px solid ${({theme}) => theme.color.subColor};
    outline-offset: -1px;
  }
`;

const MenuIcon = styled.span`
  display: inline-flex;
  width: ${({$support}) => ($support ? "18px" : "22px")};
  height: ${({$support}) => ($support ? "18px" : "22px")};
  align-items: center;
  justify-content: center;
  justify-self: center;
  color: inherit;
  opacity: ${({$active}) => ($active ? 1 : .62)};
  transition: color .15s ease, opacity .15s ease;

  svg {
    width: ${({$support}) => ($support ? "18px" : "22px")};
    height: ${({$support}) => ($support ? "18px" : "22px")};
  }
`;

const HelpGlyph = styled.span`
  display: inline-flex;
  width: 17px;
  height: 17px;
  align-items: center;
  justify-content: center;
  border: 1.5px solid currentColor;
  border-radius: 50%;
  font-size: var(--font-size-13);
  font-weight: 800;
  line-height: 1;
`;

const MenuLabel = styled.span`
  min-width: 0;
  color: inherit;
  font-size: ${({$support}) => ($support ? "var(--font-size-12)" : "var(--font-size-14)")};
  font-weight: 600;
  line-height: 1.3;
  opacity: ${({$collapsed}) => ($collapsed ? 0 : 1)};
  white-space: nowrap;
`;

const DropIcon = styled.span`
  width: 7px;
  height: 7px;
  justify-self: center;
  border-right: 1.5px solid currentColor;
  border-bottom: 1.5px solid currentColor;
  transform: rotate(${({$open}) => ($open ? "225deg" : "45deg")});
  transition: transform .18s ease;
`;

const SubMenu = styled.div`
  display: ${({$active, $collapsed}) => ($active && !$collapsed ? "flex" : "none")};
  flex-direction: column;
  gap: 3px;
  padding: 6px 8px 10px 44px;
`;

const SubMenuItem = styled.div`
  position: relative;
  display: flex;
  min-height: 34px;
  align-items: stretch;
  border-radius: 6px;
  background: ${({$selected}) => ($selected ? "rgba(59, 130, 246, .12)" : "transparent")};

  ${({$selected}) => $selected && css`
    &::before {
      position: absolute;
      top: 50%;
      left: -13px;
      width: 5px;
      height: 5px;
      border-radius: 50%;
      background: ${({theme}) => theme.color.subColor};
      content: "";
      transform: translateY(-50%);
    }
  `}
`;

const SubMenuLink = styled(Link)`
  display: flex;
  min-width: 0;
  flex: 1;
  align-items: center;
  padding: 9px 8px;
  border-radius: 6px;
  color: ${({$selected}) => ($selected ? "var(--color-surface)" : "rgba(255, 255, 255, .58)")};
  font-size: var(--font-size-13);
  font-weight: ${({$selected}) => ($selected ? 700 : 500)};
  line-height: 1.35;
  text-decoration: none;
  white-space: normal;
  word-break: keep-all;

  &:hover {
    background: rgba(59, 130, 246, .1);
    color: var(--color-surface);
  }
`;

const NestedMenuGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const SubMenuToggle = styled.button`
  display: flex;
  width: 100%;
  min-width: 0;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 9px 8px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: ${({$selected}) => ($selected ? "var(--color-surface)" : "rgba(255, 255, 255, .58)")};
  font-size: var(--font-size-13);
  font-weight: ${({$selected}) => ($selected ? 700 : 500)};
  text-align: left;
  cursor: pointer;

  &:hover {background: rgba(59, 130, 246, .1);color: var(--color-surface);}
`;

const NestedArrow = styled.span`
  width: 6px;
  height: 6px;
  flex: 0 0 auto;
  border-right: 1.5px solid currentColor;
  border-bottom: 1.5px solid currentColor;
  transform: rotate(${({$open}) => ($open ? "225deg" : "45deg")});
  transition: transform .18s ease;
`;

const NestedSubMenu = styled.div`
  display: ${({$open}) => ($open ? "flex" : "none")};
  flex-direction: column;
  gap: 2px;
  margin: 2px 0 5px 8px;
  padding-left: 10px;
  border-left: 1px solid rgba(255,255,255,.12);
`;

const NestedSubMenuLink = styled(Link)`
  display: block;
  padding: 7px 8px;
  border-radius: 5px;
  background: ${({$selected}) => ($selected ? "rgba(59,130,246,.16)" : "transparent")};
  color: ${({$selected}) => ($selected ? "var(--color-surface)" : "rgba(255,255,255,.48)")};
  font-size: var(--font-size-12);
  font-weight: ${({$selected}) => ($selected ? 700 : 500)};
  line-height: 1.35;
  text-decoration: none;
  word-break: keep-all;

  &:hover {background: rgba(59,130,246,.1);color: var(--color-surface);}
`;

const AsideFooter = styled.div`
  flex: 0 0 auto;
  padding: 10px;
  border-top: 1px solid rgba(255, 255, 255, .08);
`;

const CollapseButton = styled.button`
  display: flex;
  width: 100%;
  min-height: 40px;
  align-items: center;
  justify-content: ${({$collapsed}) => ($collapsed ? "center" : "flex-start")};
  gap: 9px;
  padding: ${({$collapsed}) => ($collapsed ? "0" : "0 11px")};
  border: 0;
  border-radius: 7px;
  background: transparent;
  color: rgba(255, 255, 255, .46);

  &:hover {
    background: rgba(255, 255, 255, .06);
    color: var(--color-surface);
  }
`;

const CollapseIcon = styled.span`
  position: relative;
  width: 25px;
  height: 25px;
  flex: 0 0 25px;
  border: 1px solid rgba(255, 255, 255, .12);
  border-radius: 7px;

  &::before {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 7px;
    height: 7px;
    border-left: 1.5px solid currentColor;
    border-bottom: 1.5px solid currentColor;
    content: "";
    transform: translate(-35%, -50%) rotate(${({$collapsed}) => ($collapsed ? "225deg" : "45deg")});
  }
`;

const CollapseText = styled.span`
  overflow: hidden;
  font-size: var(--font-size-12);
  font-weight: 600;
  opacity: ${({$collapsed}) => ($collapsed ? 0 : 1)};
  white-space: nowrap;
`;
